<script setup lang="ts">
import type { Schemas } from '#shopware'
import type { PromotionData } from '~/composables/usePromotion'
import { getCategoryRoute } from '@shopware/helpers'

const props = defineProps<{
    config: Schemas['CategorySlide']
    index: number
    promotion?: PromotionData | null
}>()

const slideContentCategoryEl = ref<HTMLDivElement | null>(null)
const isImageScaled = ref(false)

const { selectPromotion, computedPromotionData } = usePromotionObserver(
    slideContentCategoryEl,
    props.promotion ?? null,
    props.index + 1
)

// CMS slide data omits the Category `type` field, so `getCategoryRoute()` short-circuits
// to '/' on its `case void 0` branch. Default to 'page' so it falls through to the
// seoPathInfo branch and returns the proper SEO URL.
const categoryRoute = computed(() => getCategoryRoute({
    ...props.config,
    type: props.config.type ?? 'page',
}))

function onElClick(event: MouseEvent) {
    if (computedPromotionData?.value) selectPromotion(computedPromotionData.value, event)
}
</script>

<template>
    <div ref="slideContentCategoryEl" class="relative aspect-[4/5]">
        <MiscLink :to="categoryRoute" @click="onElClick($event)">
            <div class="category-slider-image-container relative h-full w-full overflow-hidden rounded-xs">
                <MiscImage
                    class="w-full h-full object-cover hover:scale-110 transition-all duration-500 cursor-pointer"
                    :class="{ 'scale-110': isImageScaled }"
                    :src="config.media?.path"
                    :alt="(config.translated?.name ?? '') + ' ' + $t('structure.slot.categorySlider.image')"
                    width="348"
                    height="435"
                    sizes="286px sm:347px lg:328px xl:398px 2xl:508px"
                    lazy-load
                />
            </div>
        </MiscLink>
        <MiscLink
            :to="categoryRoute"
            @click="onElClick($event)"
            @mouseenter="isImageScaled = true"
            @focus="isImageScaled = true"
            @mouseleave="isImageScaled = false"
            @blur="isImageScaled = false"
        >
            <div class="pt-2 pointer-events-none text-xl font-bold text-base-content">
                {{ config.translated?.name }}
            </div>
        </MiscLink>
    </div>
</template>
